import { tsMs } from './tts-debug';

/**
 * On-screen diagnostics for read-along audio, gated behind `?ttsdiag=1`.
 *
 * This exists because the device that reproduces MIC-652 is a phone, and a
 * phone has no usable JavaScript console — Safari Web Inspector needs a cabled
 * Mac with Web Inspector enabled on the device, which is exactly the setup
 * that tends not to cooperate when you need it. Every diagnostic that would
 * otherwise be a console command is a tap or a readout here instead.
 *
 * Never shown unless explicitly asked for, and remembered once asked for so it
 * survives the guest join-flow navigation.
 */
const STORAGE_KEY = 'ttsDiag';
const MAX_LINES = 14;

export class TtsDebugOverlay {
  private root: HTMLElement | null = null;
  private logEl: HTMLElement | null = null;
  private levelEl: HTMLElement | null = null;
  private lines: string[] = [];
  private mediaEl: HTMLAudioElement | null = null;
  private findTimer: number | null = null;
  private tickTimer: number | null = null;
  private tone: { ctx: AudioContext; osc: OscillatorNode; gain: GainNode } | null = null;
  private level = -50;

  constructor(private readonly document: Document) {}

  /** `?ttsdiag=1` turns it on and is remembered; `?ttsdiag=0` turns it off. */
  static enabled(): boolean {
    try {
      const raw = new URLSearchParams(location.search).get('ttsdiag');
      if (raw === '1' || raw === 'true') {
        localStorage.setItem(STORAGE_KEY, '1');
        return true;
      }
      if (raw === '0' || raw === 'false') {
        localStorage.removeItem(STORAGE_KEY);
        return false;
      }
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  }

  start(initialLevelDb: number): void {
    if (this.root) return;
    this.level = initialLevelDb;
    try {
      this.build();
      this.findMediaElement();
      this.tickTimer = setInterval(() => this.renderHeader(), 1000) as unknown as number;
    } catch (err) {
      console.warn(`[TTS ${tsMs()}] diag overlay failed`, err);
    }
  }

  stop(): void {
    if (this.findTimer !== null) {
      clearInterval(this.findTimer);
    }
    if (this.tickTimer !== null) {
      clearInterval(this.tickTimer);
    }
    this.findTimer = null;
    this.tickTimer = null;
    this.stopTone();
    this.root?.remove();
    this.root = null;
    this.logEl = null;
    this.levelEl = null;
    this.mediaEl = null;
  }

  private build(): void {
    const root = this.document.createElement('div');
    root.setAttribute('data-tts-diag', '');
    root.style.cssText = [
      'position:fixed', 'left:0', 'right:0', 'bottom:0', 'z-index:2147483647',
      'background:rgba(0,0,0,.86)', 'color:#0f0', 'font:11px/1.35 ui-monospace,monospace',
      'padding:6px 8px', 'max-height:46vh', 'overflow:auto',
      '-webkit-user-select:text', 'user-select:text',
    ].join(';');

    const level = this.document.createElement('div');
    level.style.cssText = 'color:#ff0;margin-bottom:4px;white-space:pre-wrap';
    root.appendChild(level);

    const bar = this.document.createElement('div');
    bar.style.cssText = 'display:flex;gap:6px;flex-wrap:wrap;margin-bottom:4px';
    bar.appendChild(this.button('quieter -5dB', () => this.nudge(-5)));
    bar.appendChild(this.button('louder +5dB', () => this.nudge(+5)));
    bar.appendChild(this.button('tone', () => this.toggleTone()));
    bar.appendChild(this.button('clear', () => {
      this.lines = [];
      this.renderLog();
    }));
    bar.appendChild(this.button('hide', () => {
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      this.stop();
    }));
    root.appendChild(bar);

    const log = this.document.createElement('div');
    log.style.cssText = 'white-space:pre-wrap';
    root.appendChild(log);

    this.document.body.appendChild(root);
    this.root = root;
    this.levelEl = level;
    this.logEl = log;
    this.renderHeader();
    this.push('diag ready — reproduce the clip, then read the lines below');
  }

  private button(label: string, onTap: () => void): HTMLButtonElement {
    const b = this.document.createElement('button');
    b.textContent = label;
    b.style.cssText =
      'font:11px ui-monospace,monospace;padding:5px 8px;background:#222;' +
      'color:#0f0;border:1px solid #0f0;border-radius:3px';
    b.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      onTap();
    });
    return b;
  }

  private nudge(delta: number): void {
    const hook = (globalThis as Record<string, unknown>)['__ttsSetKeepWarmDb'];
    if (typeof hook === 'function') {
      this.level = (hook as (db: number) => number)(this.level + delta);
      this.push(`keep-warm level -> ${this.level}dBFS`);
    } else {
      this.push('keep-warm hook missing (read-along not started?)');
    }
    this.renderHeader();
  }

  /**
   * A loud tone straight from Web Audio, bypassing the app entirely. If the
   * clipping survives an obviously audible continuous tone, the sink is not
   * asleep and the cause is elsewhere — which is the one measurement that
   * separates the two live hypotheses.
   */
  private toggleTone(): void {
    if (this.tone) {
      this.stopTone();
      this.push('tone off');
      return;
    }
    try {
      const Ctor =
        (globalThis as Record<string, unknown>)['AudioContext'] ||
        (globalThis as Record<string, unknown>)['webkitAudioContext'];
      if (typeof Ctor !== 'function') {
        this.push('no AudioContext for tone');
        return;
      }
      const ctx = new (Ctor as { new (): AudioContext })();
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 200;
      const gain = ctx.createGain();
      gain.gain.value = Math.pow(10, -14 / 20);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start();
      void ctx.resume();
      this.tone = { ctx, osc, gain };
      this.push(`tone ON 200Hz -14dBFS (state=${ctx.state}) — should be clearly audible`);
    } catch (err) {
      this.push(`tone failed: ${String(err)}`);
    }
  }

  private stopTone(): void {
    if (!this.tone) return;
    try {
      this.tone.osc.stop();
      void this.tone.ctx.close();
    } catch {
      /* already gone */
    }
    this.tone = null;
  }

  /** The TTS element is created but not identified, so find it by exclusion:
   *  the keep-warm element loops, the TTS element does not. */
  private findMediaElement(): void {
    const attach = () => {
      const found = Array.from(this.document.querySelectorAll('audio')).find(
        (el) => !(el as HTMLAudioElement).loop,
      ) as HTMLAudioElement | undefined;
      if (!found) return false;
      this.mediaEl = found;
      for (const ev of ['waiting', 'playing', 'stalled', 'pause', 'play', 'seeking', 'seeked']) {
        found.addEventListener(ev, () => this.push(`${ev} ${this.mediaState()}`));
      }
      this.push(`attached to TTS element ${this.mediaState()}`);
      return true;
    };
    if (attach()) return;
    this.findTimer = setInterval(() => {
      if (attach() && this.findTimer !== null) {
        clearInterval(this.findTimer);
        this.findTimer = null;
      }
    }, 500) as unknown as number;
  }

  private mediaState(): string {
    const a = this.mediaEl;
    if (!a) return '(no element)';
    const ranges: string[] = [];
    for (let i = 0; i < a.buffered.length; i++) {
      ranges.push(`[${a.buffered.start(i).toFixed(2)},${a.buffered.end(i).toFixed(2)}]`);
    }
    return `ct=${a.currentTime.toFixed(3)} paused=${a.paused} rs=${a.readyState} ` +
      `buf=${ranges.join(' ') || '(none)'}`;
  }

  private renderHeader(): void {
    if (!this.levelEl) return;
    const nav = navigator as Navigator & { audioSession?: { type: string } };
    const session = nav.audioSession ? nav.audioSession.type : 'unsupported';
    this.levelEl.textContent =
      `TTS DIAG   keep-warm ${this.level}dBFS   audioSession=${session}   ` +
      `tone=${this.tone ? 'on' : 'off'}\n${this.mediaState()}`;
  }

  private push(line: string): void {
    this.lines.push(`${tsMs()} ${line}`);
    if (this.lines.length > MAX_LINES) {
      this.lines = this.lines.slice(-MAX_LINES);
    }
    this.renderLog();
  }

  private renderLog(): void {
    if (this.logEl) {
      this.logEl.textContent = this.lines.join('\n');
    }
  }
}
